import React from 'react';
import styled from 'styled-components';
import { createSelector } from '@reduxjs/toolkit';

import type { ICalendar, IWeek, IWeekDay } from '@/types/calendar';

import { useAppSelector, useWeek } from '@/hooks';
import { otherColors } from '@/styles/colors';
import { media } from '@/styles/theme';
import { CalendarDisplayRange, WeekDays } from '@/store/enums';
import {
  HOUR_IN_PX,
  LINE_IN_PX,
  MIN_WIDTH_WEEKDAY,
  SET_NEXT_START_DAY_INDEX,
  SET_PREVIOUS_START_DAY_INDEX,
  WEEKDAY_HEADER_HEIGHT_MOBILE,
  Z_INDEX_WEEKDAY_HEADER,
  Z_INDEX_WEEKDAY_HEADER_AFTER,
} from '@/config/variables';
import {
  ArrowComponent,
  CalendarWeekContainerSkeleton,
  CurrentTimeIndicator,
  LoaderWrapper,
  StickyShadow,
} from '@/components/common';
import { classList, getCalendarMessageHeight, getHoursInDay, maxBy } from '@/helpers';
import { selectCalendarDayWidth } from '@/store/selectors/utilsSelectors';

const calendarWeekContainerSelector = createSelector(selectCalendarDayWidth, calendarDayWidth => ({
  calendarDayWidth,
}));

interface ICalendarWeekContainer {
  daysToDisplay: IWeekDay[];
  Weekday: ICalendar['Weekday'];
  isLoading: ICalendar['isLoading'];
  week: IWeek;
  isDLSMessageVisible: boolean;
  ignoreDLS: boolean | undefined;
}

const CalendarWeekContainer = ({
  daysToDisplay,
  Weekday,
  isLoading,
  week,
  isDLSMessageVisible,
  ignoreDLS,
}: ICalendarWeekContainer): React.ReactElement => {
  const { calendarDayWidth } = useAppSelector(calendarWeekContainerSelector);
  const { selectedWeekId, selectedDays, selectStartDayIndex, selectedOffset } = useWeek();

  const columnWidth = Math.max(maxBy(calendarDayWidth, 'width')?.width || 0, MIN_WIDTH_WEEKDAY);

  return (
    <WeekContainer data-selected-days={selectedDays}>
      <LoaderWrapper
        isLoading={isLoading || !selectedWeekId || daysToDisplay.length === 0}
        loader={<CalendarWeekContainerSkeleton columnCount={selectedDays} columnWidth={columnWidth} />}
      >
        {daysToDisplay.map((day, index) => (
          // 'data-day' is used for detecting shift modal collisions
          <WeekdayColumn
            className={classList(day.isCurrentDay && 'current-day', day.isDlsDay && 'dls-day')}
            data-day={day.dayName}
            minWidth={columnWidth}
            key={day.dayNumber}
          >
            <CurrentTimeIndicator
              isTzVisible={!ignoreDLS && selectedDays === CalendarDisplayRange.ONE_DAY && day.isDlsDay}
            />

            <WeekdayHeader isMessageDisplayed={isDLSMessageVisible}>
              {index === 0 && (
                <ArrowComponent
                  className="left-arrow"
                  onClick={() => selectStartDayIndex(SET_PREVIOUS_START_DAY_INDEX)}
                  hidden={selectedDays === CalendarDisplayRange.WEEK}
                  data-disabled={selectedOffset === WeekDays.Monday}
                />
              )}
              <WeekdayNumber>{day.dayNumber}</WeekdayNumber>
              <WeekdayName>{day.dayName}</WeekdayName>
              {index === selectedDays - 1 && (
                <ArrowComponent
                  className="right-arrow"
                  onClick={() => selectStartDayIndex(SET_NEXT_START_DAY_INDEX)}
                  hidden={selectedDays === CalendarDisplayRange.WEEK}
                  data-disabled={
                    (selectedDays === CalendarDisplayRange.THREE_DAYS && selectedOffset === WeekDays.Friday) ||
                    selectedOffset === WeekDays.Sunday
                  }
                />
              )}
            </WeekdayHeader>

            <StickyShadow isCalendarMessageDisplayed={isDLSMessageVisible} />

            <WeekdayBody hoursInDay={getHoursInDay(day, week, selectedDays)}>
              <StickyShadow.Cover />
              {Weekday && <Weekday week={week} day={day} isCalendarMessageDisplayed={isDLSMessageVisible} />}
            </WeekdayBody>
          </WeekdayColumn>
        ))}
      </LoaderWrapper>
    </WeekContainer>
  );
};

export { CalendarWeekContainer };

const WeekContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  flex-wrap: nowrap;
  top: 0;
  position: relative;
`;

const WeekdayColumn = styled.div<{ minWidth: number }>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: auto;
  border-radius: 20px;
  min-width: ${props => props.minWidth}px;
  border-right: 1px dashed ${otherColors.white};
  background-origin: padding-box, border-box;
  background-repeat: no-repeat, no-repeat;
  background-image: linear-gradient(${otherColors.white}, ${otherColors.white}),
    linear-gradient(
      to bottom,
      ${otherColors.white},
      ${otherColors.lxc.blue} 10%,
      ${otherColors.lxc.blue} 90%,
      ${otherColors.white}
    );

  &:last-child {
    margin-right: 0;
    border-right: 0;
  }

  &.current-day {
    background-image: linear-gradient(${otherColors.calendar.currentDay}, ${otherColors.calendar.currentDay}),
      linear-gradient(
        to bottom,
        ${otherColors.white},
        ${otherColors.lxc.blue} 10%,
        ${otherColors.lxc.blue} 90%,
        ${otherColors.white}
      );
  }

  &.dls-day {
    background-image: linear-gradient(${otherColors.calendar.dlsDay}, ${otherColors.calendar.dlsDay}),
      linear-gradient(
        to bottom,
        ${otherColors.white},
        ${otherColors.lxc.blue} 10%,
        ${otherColors.lxc.blue} 90%,
        ${otherColors.white}
      );
  }
`;

const WeekdayHeader = styled.div<{ isMessageDisplayed?: boolean }>`
  position: sticky;
  top: ${({ isMessageDisplayed }) => `${getCalendarMessageHeight(isMessageDisplayed, true)}px`};
  z-index: ${Z_INDEX_WEEKDAY_HEADER};
  height: ${WEEKDAY_HEADER_HEIGHT_MOBILE};
  user-select: none;

  text-align: center;
  color: ${otherColors.calendar.text};
  background-color: #fff;

  transition: height 0.2s linear;

  ${media.tablet} {
    height: 143px;
    top: ${({ isMessageDisplayed }) => `${getCalendarMessageHeight(isMessageDisplayed)}px`};
  }

  &::before,
  &::after {
    content: '';
    position: absolute;
    height: 20px;
    width: 100%;
    left: 0;
    top: 0;
  }

  &::before {
    background-color: #fff;
  }

  &::after {
    border-top-left-radius: 25px;
    border-top-right-radius: 25px;
    background-color: #fff;
    z-index: ${Z_INDEX_WEEKDAY_HEADER_AFTER};
  }

  .current-day & {
    color: ${otherColors.calendar.currentTime};
    background: ${otherColors.calendar.currentDay};

    &::after {
      background-color: ${otherColors.calendar.currentDay};
    }
  }

  .dls-day & {
    color: ${otherColors.calendar.dlsTime};
    background: ${otherColors.calendar.dlsDay};

    &::after {
      background-color: ${otherColors.calendar.dlsDay};
    }
  }
`;

const WeekdayNumber = styled.div`
  margin-top: 20px;
  margin-bottom: 0;
  line-height: 1;
  font-size: 34px;
  font-weight: 100;

  transition: margin 0.2s linear, font-size 0.2s linear;

  ${media.tablet} {
    margin-top: 30px;
    margin-bottom: 12px;
    font-size: 50px;
  }
`;

const WeekdayName = styled.div`
  font-size: 12px;
  font-weight: 400;

  transition: font-size 0.2s linear;

  ${media.tablet} {
    font-size: 14px;
  }
`;

const WeekdayBody = styled.div<{ hoursInDay: number }>`
  position: relative;
  height: ${({ hoursInDay }) => HOUR_IN_PX * hoursInDay}px;
  background-image: repeating-linear-gradient(
    180deg,
    ${otherColors.calendar.hGrid},
    ${otherColors.calendar.hGrid} ${LINE_IN_PX}px,
    transparent ${LINE_IN_PX}px,
    transparent ${HOUR_IN_PX}px
  );

  .current-day & {
    background-image: repeating-linear-gradient(
      180deg,
      ${otherColors.calendar.currentDayHGrid},
      ${otherColors.calendar.currentDayHGrid} ${LINE_IN_PX}px,
      transparent ${LINE_IN_PX}px,
      transparent ${HOUR_IN_PX}px
    );
  }

  .dls-day & {
    background-image: repeating-linear-gradient(
      180deg,
      ${otherColors.calendar.dlsDayHGrid},
      ${otherColors.calendar.dlsDayHGrid} ${LINE_IN_PX}px,
      transparent ${LINE_IN_PX}px,
      transparent ${HOUR_IN_PX}px
    );
  }
`;
